import React, { ChangeEvent, FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Col, Form, FormInstance, Input, Row, Select, Space, Typography } from 'antd'
import { getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input'
import { useAuthContext } from '../../../../context/auth'
import Countries from '../../../../utils/countries'
import { TState } from '../../../../context/types'
import { useCountriesContext } from '../../../../context/countries'
import { getStatesByCountry } from '../../../../context/states'
import ISO6391 from 'iso-639-1'
import { FormListFieldData } from 'antd/lib/form/FormList'
import * as networks from 'react-social-icons/build/networks'
import { SocialIcon } from 'react-social-icons'
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { SocialLinks } from 'social-links'
import { languagesOptions } from '../../../../utils/languages'
import { useChannelContext } from '../../../../context/channel'
import { REGEX_CITY } from '../../../../constants'

const { Option } = Select
const { Title } = Typography
const socialLinks = new SocialLinks()
const regex = new RegExp(/[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi)

type TProps = {
	form: FormInstance
}

const WelcomeMember: FunctionComponent<TProps> = ({ form }) => {
	const { auth } = useAuthContext()
	const {
		countries: { data: countries, loading: countryLoading },
		actions: countryActions,
	} = useCountriesContext()

	const [states, setStates] = useState<TState[]>([])
	const [stateSearch, setStateSearch] = useState<string>('')
	const [countrySearch, setCountrySearch] = useState<string>('')
	const [links, setLinks] = useState<string[]>([])
	const { channel } = useChannelContext()

	useEffect(() => {
		countryActions.getList()
	}, [])

	useEffect(() => {
		if (auth?.user?.country?.id) {
			getStatesByCountry(auth?.user?.country?.id)
				.then((stateData: TState[]) => setStates(stateData))
				.catch()
		}
	}, [auth?.user?.country?.id])

	const filterSort = (search: string) => (optionA: any, optionB: any): number => {
		const indexA = optionA.children.toLowerCase().indexOf(search.toLowerCase())
		const indexB = optionB.children.toLowerCase().indexOf(search.toLowerCase())

		return indexA < indexB ? -1 : indexA > indexB ? 1 : 0
	}

	const triggerValidatePhoneField = useCallback(
		(option) => {
			form.setFieldsValue({
				phone_country_id: getCountryCallingCode(option),
			})
			form.validateFields(['phone'])
		},
		[form]
	)

	const handleCountryChange = (value: string) => {
		form.setFieldsValue({ stateId: null })
		getStatesByCountry(value)
			.then((stateData: TState[]) => setStates(stateData))
			.catch()
	}

	const onChangeUrl = (idx: number) => (e: ChangeEvent<HTMLInputElement>) => {
		const newLinks = [...links]
		newLinks[idx] = e.target.value
		setLinks(newLinks)
	}

	const onLanguagesSearch = (searchText: string, option: any) => new RegExp(searchText, 'gi').test(option.children)

	const prefixSelector = (
		<>
			<Form.Item name="phone_country_code" noStyle>
				<Select
					filterOption={(input, option) => {
						return (
							option['data-code'].toLowerCase().indexOf(input.toLowerCase().replace('+', '')) >= 0 ||
							option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
						)
					}}
					showSearch
					style={{ width: 105 }}
					onChange={triggerValidatePhoneField}
				>
					{Countries.options.map(({ code, flag, country }, index) => (
						<Option key={index} value={`${country}`} data-code={code}>
							<span className="country-flag">{flag}</span>
							<span className="country-code">+{code}</span>
						</Option>
					))}
				</Select>
			</Form.Item>
			<Form.Item name="phone_country_id" style={{ display: 'none' }}>
				<Input disabled={!!auth.loading} />
			</Form.Item>
		</>
	)

	const isPhoneRequired = useMemo(
		() =>
			auth.user?.isExpert
				? (channel?.channels || []).reduce(
						(acc, { integration_config, is_expert }) =>
							acc || (is_expert && (integration_config?.phone_rules?.phone_required_to_register_expert ?? true)),
						false
				  )
				: (channel?.channels || []).reduce(
						(acc, { integration_config, is_expert }) =>
							acc || (!is_expert && integration_config?.phone_rules?.phone_required_to_register_member),
						false
				  ),
		[channel?.channels?.length, auth.user?.isExpert]
	)

	return (
		<div className="welcome-member-step">
			<Row gutter={[50, 0]}>
				<Col xs={24} lg={24}>
					<Title level={4}>Where are You located?</Title>
				</Col>
				<Col xs={24} lg={8}>
					{!countryLoading ? (
						<Form.Item name="countryId" label="Country">
							<Select
								showSearch
								onChange={handleCountryChange}
								optionFilterProp="name"
								onSearch={(input: string) => setCountrySearch(input)}
								filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
								filterSort={filterSort(countrySearch)}
							>
								{countries.map((country: any, index) => (
									<Option key={index} value={country.id}>
										{country.name}
									</Option>
								))}
							</Select>
						</Form.Item>
					) : null}
				</Col>
				<Col xs={24} lg={8}>
					<Form.Item
						label="City"
						name="city"
						rules={[
							{
								message: 'Please enter correct name',
								validator: (_, value) => {
									const regex = new RegExp(REGEX_CITY, 'ig')
									if (!regex.test(value)) {
										return Promise.reject()
									}
									return Promise.resolve()
								},
							},
						]}
					>
						<Input disabled={!!auth.loading} />
					</Form.Item>
				</Col>
				<Col xs={24} lg={8}>
					{!countryLoading && states.length ? (
						<Form.Item name="stateId" label="State / Province">
							<Select
								showSearch
								optionFilterProp="name"
								filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
								onSearch={(input: string) => setStateSearch(input)}
								filterSort={filterSort(stateSearch)}
							>
								{states?.map((state: any, index) => (
									<Option key={index} value={state.id}>
										{state.name}
									</Option>
								))}
							</Select>
						</Form.Item>
					) : null}
				</Col>
			</Row>
			<Row gutter={[50, 0]}>
				<Col xs={24} lg={8}>
					<Form.Item
						name="phone"
						label="Mobile Number"
						rules={[
							{
								validator: (_rule, value) => {
									const phoneCountryId = getCountryCallingCode(form.getFieldValue('phone_country_code'))
									if (!value || isValidPhoneNumber(`+${phoneCountryId}${value}`)) {
										return Promise.resolve()
									}
									return Promise.reject('The input is not valid mobile number!')
								},
								message: 'The input is not valid mobile number!',
							},
							{ required: isPhoneRequired, message: 'Please input your Mobile number!' },
						]}
					>
						<Input addonBefore={prefixSelector} style={{ width: '100%' }} type="number" disabled={!!auth.loading} />
					</Form.Item>
				</Col>
				<Col xs={24} lg={8}>
					<Form.Item
						label="Language"
						name="languages"
						rules={[{ required: auth.user?.isExpert, message: 'Please input preferred languages.' }]}
					>
						<Select mode="multiple" allowClear filterOption={onLanguagesSearch}>
							{languagesOptions.map((iso: string) => {
								return (
									<Option key={iso} value={iso}>
										{ISO6391.getName(iso)}
									</Option>
								)
							})}
						</Select>
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={[50, 0]}>
				<Col xs={24} lg={10}>
					<Space direction="vertical" style={{ width: '100%' }}>
						<Title level={4}>Social Links</Title>
						<Form.List name="socialLinks">
							{(fields: FormListFieldData[], { add, remove }) => (
								<>
									{fields.map(({ key, name, fieldKey, ...restField }) => (
										<Row key={key} align="bottom" gutter={[8, 0]}>
											<Col flex="auto">
												<Form.Item
													{...restField}
													name={[name, 'url']}
													fieldKey={[fieldKey, 'url']}
													rules={[
														{ required: true, message: 'Missing Url' },
														{
															message: 'Not valid Url!',
															validator: (_, value) => {
																if (!socialLinks.isValid(networks.keyFor(value), value || '') && !value?.match(regex)) {
																	return Promise.reject()
																}
																return Promise.resolve()
															},
														},
													]}
												>
													<Input
														prefix={
															<SocialIcon
																style={{ height: 25, width: 25 }}
																url={links[name] || ''}
																onClick={(e: { preventDefault: () => any }) => e.preventDefault()}
															/>
														}
														placeholder="Url"
														onChange={onChangeUrl(name)}
													/>
												</Form.Item>
											</Col>
											<Col flex="none">
												<CloseCircleOutlined
													onClick={() => remove(name)}
													style={{ fontSize: '18px', marginBottom: 31 }}
												/>
											</Col>
										</Row>
									))}
									<Row>
										<Col span={24}>
											<Form.Item>
												<Button type="ghost" onClick={() => add()} icon={<PlusOutlined />}>
													Add Link
												</Button>
											</Form.Item>
										</Col>
									</Row>
								</>
							)}
						</Form.List>
					</Space>
				</Col>
			</Row>
		</div>
	)
}

export default WelcomeMember
